'use client';
import { alpha, Box, Button, Theme } from '@mui/material';

import type { EventOutcome } from '@/types';
import { formatProbability } from '@/utils';
import { OutcomeBarRow } from './OutcomeBarRow';
import type { CardVariantProps } from './types';

// Desktop: two big equal-width outcome buttons (matches the Yes/No binary
// card's big-button treatment). Sizing/typography matches TradeButtons' big
// Yes/No pair so every "two large buttons" card in the app looks the same.
const getBigButtonStyles = (theme: Theme, idx: number) => {
  const variant = idx === 0 ? theme.palette.success : theme.palette.error;
  return {
    py: 0.875,
    flex: 1,
    minWidth: 0,
    borderRadius: '9px',
    textTransform: 'none' as const,
    fontFamily: 'var(--font-archivo), Archivo, sans-serif',
    fontWeight: 600,
    fontSize: '13.5px',
    bgcolor: variant.bg,
    color: theme.palette.mode === 'dark' ? variant.light : variant.dark,
    border: '1px solid',
    borderColor: variant.border,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha(variant.main, 0.25),
      boxShadow: 'none',
    },
  };
};

export function CustomBinaryCard({ market, onTradeClick }: CardVariantProps) {
  const outcomes = Array.isArray(market.outcomes) ? (market.outcomes as EventOutcome[]) : [];
  if (outcomes.length === 0) return null;

  const visibleOutcomes = outcomes.slice(0, 2);

  const renderVisibleOutcome = (outcome: EventOutcome, i: number) => {
    const price = Number(outcome.price ?? outcome.midpoint ?? 0);
    const handleTrade = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (market.id && outcome.id) {
        onTradeClick?.(market.id, outcome.id, true);
      }
    };

    return (
      <OutcomeBarRow
        key={outcome.id || i}
        label={outcome.name}
        price={price}
        percent={formatProbability(price, 0)}
        isYes={i === 0}
        onClick={handleTrade}
      />
    );
  };

  const renderVisibleOutcomeDesktop = (outcome: EventOutcome, i: number) => {
    const handleTrade = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (market.id && outcome.id) {
        onTradeClick?.(market.id, outcome.id, true);
      }
    };

    return (
      <Button
        key={outcome.id || i}
        onClick={handleTrade}
        sx={(theme) => getBigButtonStyles(theme, i)}
      >
        {outcome.name}
      </Button>
    );
  };

  const outcomesRows = visibleOutcomes.map(renderVisibleOutcome);
  const outcomesRowsDesktop = visibleOutcomes.map(renderVisibleOutcomeDesktop);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 'auto' }}>
      {/* Mobile: Up/Down progress-bar rows with an odds multiplier. */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column' }}>
        {outcomesRows}
      </Box>
      {/* Desktop: two big equal-width Up/Down buttons. */}
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>{outcomesRowsDesktop}</Box>
    </Box>
  );
}
